/** Server-derived time: expiry is compared against the server snapshot plus monotonic elapsed time (UX01). */
import { parseInstant } from "./format.ts";

/** A monotonic clock in milliseconds (`performance.now()` in the browser). */
export interface Mono {
  now(): number;
}

/** The server's notion of "now" at the moment an envelope was received. */
export interface ServerClock {
  readonly snapshotMicros: bigint;
  readonly receivedAt: number;
}

/** Anchor a server snapshot instant to the monotonic clock at receipt. */
export function anchor(snapshotTime: string, mono: Mono): ServerClock {
  return { snapshotMicros: parseInstant(snapshotTime), receivedAt: mono.now() };
}

/** The server's current time in microseconds: snapshot plus monotonic elapsed (never the wall clock). */
export function serverNowMicros(clock: ServerClock, mono: Mono): bigint {
  const elapsedMs = Math.max(0, mono.now() - clock.receivedAt);
  return clock.snapshotMicros + BigInt(Math.trunc(elapsedMs * 1000));
}

/** Whether `expiresAt` has passed on the server's clock; a changed browser clock cannot revive a plan. */
export function isExpired(expiresAt: string, clock: ServerClock, mono: Mono): boolean {
  return parseInstant(expiresAt) <= serverNowMicros(clock, mono);
}

/** Whole seconds left until `expiresAt` on the server's clock (zero once expired). */
export function secondsLeft(expiresAt: string, clock: ServerClock, mono: Mono): number {
  const left = parseInstant(expiresAt) - serverNowMicros(clock, mono);
  return left <= 0n ? 0 : Number(left / 1_000_000n);
}

/**
 * Whether data that was `ageUs` old at the snapshot is now older than its freshness bound, on the
 * server's clock (review F1: a fresh snapshot ages; an unknown age is never fresh).
 */
export function isAged(
  ageUs: number | null,
  boundUs: number,
  clock: ServerClock,
  mono: Mono,
): boolean {
  if (ageUs === null) {
    return true;
  }
  return BigInt(ageUs) + (serverNowMicros(clock, mono) - clock.snapshotMicros) > BigInt(boundUs);
}

/** The real monotonic clock. */
export const realMono: Mono = { now: () => performance.now() };
